import { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import Swal from "sweetalert2";
import CloseIcon from "@mui/icons-material/Close";
import request from "../../../Helpers/API";
import { SearchSelect } from "../ConfigurarLeads/SearchSelect";

// Evita que se escriba manualmente un % fuera de 0-100 (min/max del input no lo impiden al teclear).
const clampPct = (value) => {
    if (value === "") return value;
    const n = Number(value);
    if (isNaN(n)) return value;
    return String(Math.min(100, Math.max(0, n)));
};

const formInicial = {
    idusuario: "",
    porcentaje_normal: "",
    porcentaje_moratorio: "",
    notificar: false,
};

// Alta y edición de condonación de interés por usuario — mismo componente, según exista
// idConfiguracion. Al abrir, CondonacionIntereses/UsuarioCondonacionID?idAutoriza= trae tanto el
// combo de usuarios disponibles como la info ya guardada (idAutoriza=0 en alta, sin info previa).
export const CondonacionInteresModal = ({ open, onClose, idConfiguracion }) => {
    const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
    const [form, setForm] = useState(formInicial);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const esEdicion = Boolean(idConfiguracion);

    useEffect(() => {
        if (!open) return;
        setCargando(true);
        request
            .get(`CondonacionIntereses/UsuarioCondonacionID?idAutoriza=${idConfiguracion || 0}`)
            .then((result) => {
                setUsuariosDisponibles(Array.isArray(result?.usuarios) ? result.usuarios : []);
                const info = result?.infoUsuarioCondonacion;
                setForm(
                    info
                        ? {
                            idusuario: info.usuario,
                            porcentaje_normal: info.porcentaje_normal,
                            porcentaje_moratorio: info.porcentaje_moratorio,
                            notificar: Boolean(info.notificar),
                        }
                        : formInicial
                );
                setCargando(false);
            })
            .catch(() => {
                setCargando(false);
                Swal.fire("CONDONACIÓN DE INTERÉS", "No se pudo cargar la información del usuario.", "error");
            });
    }, [open, idConfiguracion]);

    const handleCancelar = () => {
        setForm(formInicial);
        onClose();
    };

    const validar = () => {
        if (!form.idusuario) {
            Swal.fire("CONDONACIÓN DE INTERÉS", "Selecciona un usuario.", "warning");
            return false;
        }
        const normal = Number(form.porcentaje_normal);
        const moratorio = Number(form.porcentaje_moratorio);
        if (form.porcentaje_normal === "" || isNaN(normal) || normal < 0 || normal > 100) {
            Swal.fire("CONDONACIÓN DE INTERÉS", "El porcentaje de interés normal debe estar entre 0 y 100.", "warning");
            return false;
        }
        if (form.porcentaje_moratorio === "" || isNaN(moratorio) || moratorio < 0 || moratorio > 100) {
            Swal.fire("CONDONACIÓN DE INTERÉS", "El porcentaje de interés moratorio debe estar entre 0 y 100.", "warning");
            return false;
        }
        return true;
    };

    const handleGuardar = () => {
        if (!validar()) return;

        const post = {
            id: esEdicion ? idConfiguracion : null,
            usuario_autoriza: form.idusuario,
            porcentaje_normal: Number(form.porcentaje_normal),
            porcentaje_moratorio: Number(form.porcentaje_moratorio),
            notificar: form.notificar,
        };

        setGuardando(true);
        request
            .post("CondonacionIntereses/Guardar", post)
            .then(() => {
                setGuardando(false);
                Swal.fire("CONDONACIÓN DE INTERÉS", "Configuración guardada correctamente.", "success");
                handleCancelar();
            })
            .catch(() => {
                setGuardando(false);
                Swal.fire("CONDONACIÓN DE INTERÉS", "No se pudo guardar la configuración. Intenta nuevamente.", "error");
            });
    };

    return (
        <Modal open={open} onClose={handleCancelar}>
            <div className="cdi-modal-overlay">
                <div className="cdi-modal-box">
                    <div className="cdi-modal-header">
                        <h3>{esEdicion ? "Editar configuración" : "Nueva configuración"}</h3>
                        <button type="button" className="cdi-modal-close" onClick={handleCancelar}>
                            <CloseIcon />
                        </button>
                    </div>

                    <div className="cdi-modal-body">
                        <div className="cdi-form-group">
                            <label>Usuario</label>
                            <SearchSelect
                                options={usuariosDisponibles.map((u) => ({ value: u.idusuario, label: u.usuario }))}
                                value={form.idusuario}
                                onChange={(val) => setForm((prev) => ({ ...prev, idusuario: val }))}
                                placeholder={cargando ? "Cargando usuarios..." : "Buscar usuario..."}
                            />
                        </div>

                        <div className="cdi-form-row">
                            <div className="cdi-form-group">
                                <label>% máx. interés normal</label>
                                <div className="cdi-input-pct">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step="0.01"
                                        value={form.porcentaje_normal}
                                        onChange={(e) => setForm((prev) => ({ ...prev, porcentaje_normal: clampPct(e.target.value) }))}
                                        placeholder="0"
                                    />
                                    <span>%</span>
                                </div>
                            </div>

                            <div className="cdi-form-group">
                                <label>% máx. interés moratorio</label>
                                <div className="cdi-input-pct">
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        step="0.01"
                                        value={form.porcentaje_moratorio}
                                        onChange={(e) => setForm((prev) => ({ ...prev, porcentaje_moratorio: clampPct(e.target.value) }))}
                                        placeholder="0"
                                    />
                                    <span>%</span>
                                </div>
                            </div>
                        </div>

                        <label className="cdi-switch-row">
                            <span className="cdi-switch">
                                <input
                                    type="checkbox"
                                    checked={form.notificar}
                                    onChange={(e) => setForm((prev) => ({ ...prev, notificar: e.target.checked }))}
                                />
                                <span className="cdi-switch-slider" />
                            </span>
                            Solicitar autorización a este usuario
                        </label>
                    </div>

                    <div className="cdi-modal-footer">
                        <button type="button" className="cdi-btn-cancelar" onClick={handleCancelar}>
                            Cancelar
                        </button>
                        <button type="button" className="cdi-btn-guardar" onClick={handleGuardar} disabled={guardando || cargando}>
                            {guardando ? "Guardando..." : "Guardar"}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default CondonacionInteresModal;
